/* eslint-disable react/jsx-key */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
import React, { useEffect, useState } from 'react';
import { Collapse, Button, Tabs, Modal } from 'antd';
import './ShipmentTrack.css';
import { LeftOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { useAuth } from '@isocel/front-common';

import { usePackagingFlow } from '../../context/PackagingFlowContext';
import { usePackagingConfig } from '../../queries/package';
import { BaseModel, Package, PackagingFlowDirection, PackagingFlowItem } from '../../queries/api/types';
import defaultImage from '../../assets/images/default.png';
import { PackageIcon } from '../../components/PackageIcon';
import { useNotification } from '../notification/Notification';

const { Panel } = Collapse;
const { TabPane } = Tabs;

const ShipmentTrack: React.FC = () => {
    const history = useHistory();
    const [pkgItemMap, setPkgItemMap] = useState<Record<string, PackagingFlowItem>>({});
    const [isModalVisible, setIsModalVisible] = useState(false);
    const { showNotification } = useNotification();

    const {
        loadingSession,
        selectedStore,
        setPackageFlow,
        packageFlow,
        voucher,
        warehouse,
        cmr,
        flowType,
        movementType,
        selectedProvider,
        selectedParc,
        selectedSender,
        selectedReciever,
        packageToEdit,
        setPackageToEdit,
        isModifierClicked,
    } = usePackagingFlow();
    const { user } = useAuth();
    const [activeKeys, setActiveKeys] = useState<string[]>([]);
    const { data: packageConfig } = usePackagingConfig(user?.organization?.id);
    const [isPallateMovement, setPallateMovement] = useState(false);
    useEffect(() => {
        if (packageConfig) {
            const flowMap: Record<string, PackagingFlowItem> = {};
            if (!packageFlow) {
                const flowItems = packageConfig.packages.map((pkg) => {
                    const flowItem: PackagingFlowItem = {
                        package: pkg.id,
                        shippedQuantity: 0,
                        goodCount: 0,
                        badCount: 0,
                    };
                    flowMap[pkg.id] = flowItem;
                    return flowItem;
                });
                setPackageFlow({
                    sender: '',
                    recipient: '',
                    user: '',
                    loadingSession: '',
                    flowType: PackagingFlowDirection.ShippedToStore,
                    packages: flowItems,
                    cmr: cmr,
                });
            } else {
                packageFlow?.packages.forEach((pkg) => {
                    flowMap[pkg.package as string] = pkg;
                });
            }
            setPkgItemMap(flowMap);
            if (isModifierClicked) {
                const openKeys = packageConfig.packages
                    .filter(
                        (pkg) => flowMap[pkg.id]?.shippedQuantity > 0 && `${pkg.category.id}_${pkg.type.id}` // Unique key for category and type
                    )
                    .map((pkg) => `${pkg.category.id}_${pkg.type.id}`);
                setActiveKeys(openKeys);
            }
            const pallateMovement = [
                PackagingFlowDirection.Sale,
                PackagingFlowDirection.Destruction,
                PackagingFlowDirection.ShipmentForRepair,
                PackagingFlowDirection.Transfer,
                PackagingFlowDirection.Increase,
                PackagingFlowDirection.Decrease,
                PackagingFlowDirection.ReturnToProvider,
            ].includes(flowType);
            setPallateMovement(pallateMovement);
        }
    }, [
        cmr,
        flowType,
        isModifierClicked,
        isPallateMovement,
        movementType,
        packageConfig,
        packageFlow,
        selectedReciever,
        setPackageFlow,
    ]);

    const handleQuantityChange = (pkgId: string, value: number) => {
        const updatedMap = { ...pkgItemMap };
        if (!updatedMap[pkgId]) {
            updatedMap[pkgId] = { package: pkgId, shippedQuantity: 0, goodCount: 0, badCount: 0 };
        }
        updatedMap[pkgId].shippedQuantity = Math.max(0, (updatedMap[pkgId].shippedQuantity || 0) + value);
        setPkgItemMap(updatedMap);
    };

    const handleClearCount = () => {
        if (!packageFlow) return;

        if (packageToEdit) {
            const updatedPackages = packageFlow.packages.map((pkg) =>
                pkg.package === packageToEdit.id ? { ...pkg, shippedQuantity: 0 } : pkg
            );
            const clearedValue = Object.fromEntries(updatedPackages.map((pkg) => [pkg.package as string, pkg]));
            setPackageFlow({
                ...packageFlow,
                packages: updatedPackages,
            });
            setPkgItemMap(clearedValue);
        } else {
            const updatedPackages = packageFlow.packages.map((pkg) => ({
                ...pkg,
                shippedQuantity: 0,
            }));
            const clearedValue = Object.fromEntries(updatedPackages.map((pkg) => [pkg.package as string, pkg]));
            setPackageFlow({
                ...packageFlow,
                packages: updatedPackages,
            });
            setPkgItemMap(clearedValue);
        }

        setIsModalVisible(false);
    };

    const handleSave = () => {
        setPackageToEdit(null);
        if (packageFlow) {
            // First, validate packages before doing anything else
            const packagesArray = Object.values(pkgItemMap).map((pkg) => ({
                package: pkg.package,
                shippedQuantity: pkg.shippedQuantity,
                goodCount: pkg.goodCount || 0,
                badCount: pkg.badCount || 0,
            }));

            // Validate that at least one package has quantity > 0 (except for RecivedInStore)
            const hasPackages = packagesArray.some(
                (pkg) => pkg.shippedQuantity > 0 || pkg.goodCount > 0 || pkg.badCount > 0
            );
            if (!hasPackages && flowType !== PackagingFlowDirection.RecivedInStore) {
                // Don't proceed if no packages and not a RecivedInStore flow
                showNotification(
                    'error',
                    'Validation',
                    "Au moins un emballage avec une quantité supérieure à 0 est requis pour créer un flux d'expédition."
                );
                return;
            }

            let sender, recipient, senderType, recieverType;
            if (movementType && isPallateMovement) {
                sender = selectedParc?.id;
                recipient = selectedProvider?.id;
            }
            if (
                flowType === PackagingFlowDirection.Transfer ||
                flowType === PackagingFlowDirection.Increase ||
                flowType === PackagingFlowDirection.Decrease
            ) {
                sender = selectedSender?.id || selectedSender?._id;
                recipient = selectedReciever?.id || selectedReciever?._id;
                senderType = selectedSender?.SelectionType;
                recieverType = selectedReciever?.SelectionType;
            }
            if (flowType === PackagingFlowDirection.Destruction) {
                sender = selectedParc?.id;
                recipient = selectedParc?.id;
            }
            if (sender && recipient) {
                const req = {
                    sender,
                    recipient,
                    user: '',
                    loadingSession: '',
                    transportVoucher: '',
                    flowType: flowType,
                    packages: packagesArray,
                    comment: '',
                    cmr: cmr,
                    senderType,
                    recieverType,
                };
                setPackageFlow(req);
            } else {
                setPackageFlow(packageFlow);
            }
            history.push('/supportTracking');
        }
    };

    const renderPackage = (pkg: Package) => {
        return (
            <div key={pkg.id} className="package-item">
                <div className="package-icon-container" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <PackageIcon
                        fileId={pkg.iconRemoteFile?.id}
                        className="package-icon"
                        alt={pkg.model.name}
                        style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                        defaultImage={defaultImage}
                    />
                    <div className="modal-view" style={{ marginTop: '2px' }}>
                        <strong className="font-model ">{pkg.model.name}</strong>
                    </div>
                </div>
                <div className="quantity-controls quantity">
                    <MinusOutlined onClick={() => handleQuantityChange(pkg.id, -1)} className="minus" />
                    <input
                        type="number"
                        value={pkgItemMap[pkg.id]?.shippedQuantity || 0}
                        min="0"
                        onChange={(e) => {
                            const newValue = parseInt(e.target.value, 10) || 0;
                            handleQuantityChange(pkg.id, newValue - (pkgItemMap[pkg.id]?.shippedQuantity || 0));
                        }}
                        className="input-box"
                    />
                    <PlusOutlined onClick={() => handleQuantityChange(pkg.id, 1)} className="plus" />
                </div>
            </div>
        );
    };

    const renderTypesByCategory = (categoryId: string) => {
        const typesInCategory = packageConfig?.packages
            .filter((pkg) => pkg.category.id === categoryId)
            .reduce((acc, pkg) => {
                if (!acc.some((type) => type.id === pkg.type.id)) {
                    acc.push(pkg.type);
                }
                return acc;
            }, [] as BaseModel[]);

        return typesInCategory?.map((type) => {
            return (
                <div className="type-accordian">
                    <Collapse
                        key={`${categoryId}_${type.id}`}
                        activeKey={activeKeys}
                        onChange={(keys) => setActiveKeys(keys as string[])}
                        style={{ marginBottom: '10px' }}
                    >
                        <Panel
                            header={
                                <div className="panel-header">
                                    <PackageIcon
                                        fileId={type.iconRemoteFile?.id}
                                        className="header-type-image"
                                        alt="package-type"
                                        defaultImage={type.image || defaultImage}
                                    />
                                    <span>{type.name}</span>
                                </div>
                            }
                            key={`${categoryId}_${type.id}`}
                        >
                            {packageConfig?.packages
                                .filter((pkg) => pkg.category.id === categoryId && pkg.type.id === type.id)
                                .map((pkg) => renderPackage(pkg))}
                        </Panel>
                    </Collapse>
                </div>
            );
        });
    };

    return (
        <>
            <div className="title mb-16">
                <LeftOutlined className="back-icon" onClick={() => history.goBack()} />
            </div>
            <div className="shipment-track bg-dash">
                <header className="header">
                    <div className="location">
                        {isPallateMovement && movementType ? (
                            ['Transfer', 'Increase', 'Decrease'].includes(movementType.name) ? (
                                <>
                                    <p>{movementType?.label}</p>
                                    <p>Émetteur: {selectedSender?.name}</p>
                                    <p>Destinataire: {selectedReciever?.name}</p>
                                </>
                            ) : (
                                <>
                                    <p>{movementType?.label}</p>
                                    {selectedProvider?.name && <p>Prestataire: {selectedProvider.name}</p>}
                                    <p>Parc palettes: {selectedParc?.name}</p>
                                </>
                            )
                        ) : (
                            <>
                                <p>{selectedStore?.name}</p>
                                {voucher && <p>Bon de transport: {voucher}</p>}
                                {loadingSession?.trailerNumber && <p>Remorque: {loadingSession.trailerNumber}</p>}
                            </>
                        )}
                    </div>
                    <div className="info">
                        {loadingSession?.createdAt && (
                            <p>
                                {loadingSession.createdAt.toLocaleDateString()} -{' '}
                                {loadingSession.createdAt.toLocaleTimeString()}
                            </p>
                        )}
                        {cmr && <p>CMR: {cmr}</p>}
                        {warehouse && <p>Entrepôt: {warehouse.name}</p>}
                        {loadingSession?.transporter?.name && <p>Transporteur: {loadingSession.transporter.name}</p>}
                    </div>
                </header>
                <Tabs
                    defaultActiveKey={
                        packageToEdit ? packageToEdit.category.id : packageConfig?.packageCategories[0]?.id ?? '1'
                    }
                >
                    {packageToEdit ? (
                        <TabPane tab={packageToEdit.category.name} key={packageToEdit.category.id}>
                            <Collapse activeKey={`${packageToEdit.category.id}_${packageToEdit.type.id}_panel`}>
                                <Panel
                                    header={
                                        <div>
                                            <PackageIcon
                                                fileId={packageToEdit.iconRemoteFile?.id}
                                                className="header-type-image"
                                                alt="package-type"
                                                defaultImage={packageToEdit.type.image || defaultImage}
                                            />
                                            <span className="font-bold">{packageToEdit.type.name}</span>
                                        </div>
                                    }
                                    key={`${packageToEdit.category.id}_${packageToEdit.type.id}_panel`}
                                >
                                    <div key={packageToEdit.id} className="package-item">
                                        <div
                                            className="package-icon-container"
                                            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                        >
                                            <PackageIcon
                                                fileId={packageToEdit.iconRemoteFile?.id}
                                                className="package-icon"
                                                alt={packageToEdit.model.name}
                                                style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                                                defaultImage={defaultImage}
                                            />
                                            <div className="modal-view" style={{ marginTop: '2px' }}>
                                                <strong className="font-model ">{packageToEdit.model.name}</strong>
                                            </div>
                                        </div>
                                        <div className="quantity-controls quantity">
                                            <MinusOutlined
                                                onClick={() => handleQuantityChange(packageToEdit.id, -1)}
                                                className="minus"
                                            />
                                            <input
                                                type="number"
                                                value={pkgItemMap[packageToEdit.id]?.shippedQuantity || 0}
                                                min="0"
                                                onChange={(e) => {
                                                    const newValue = parseInt(e.target.value, 10) || 0;
                                                    handleQuantityChange(
                                                        packageToEdit.id,
                                                        newValue - (pkgItemMap[packageToEdit.id]?.shippedQuantity || 0)
                                                    );
                                                }}
                                                className="input-box"
                                            />
                                            <PlusOutlined
                                                onClick={() => handleQuantityChange(packageToEdit.id, 1)}
                                                className="plus"
                                            />
                                        </div>
                                    </div>
                                </Panel>
                            </Collapse>
                        </TabPane>
                    ) : (
                        packageConfig?.packageCategories
                            .filter((category) => packageConfig.packages.some((pkg) => pkg.category.id === category.id))
                            .map((category) => (
                                <TabPane tab={category.name} key={category.id}>
                                    {renderTypesByCategory(category.id)}
                                </TabPane>
                            ))
                    )}
                </Tabs>
            </div>
            <div className="bottom">
                <footer className="flex items-center justify-center">
                    <Button className="mr-16" onClick={() => setIsModalVisible(true)}>
                        Réinitialiser
                    </Button>
                    <Button type="primary" onClick={handleSave}>
                        Valider
                    </Button>
                </footer>
            </div>
            <Modal
                title="Supprimer les données"
                visible={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                closable={false}
                centered
                width={360}
                footer={[
                    <div className="flex items-center justify-center">
                        <Button className="mr-16" onClick={() => setIsModalVisible(false)}>
                            Annuler
                        </Button>
                        <Button type="primary" onClick={handleClearCount}>
                            Supprimer
                        </Button>
                    </div>,
                ]}
                aria-hidden={isModalVisible}
            >
                <div className="text-black font-14">
                    Vous êtes sur le point de réinitialiser les supports et emballages. Souhaitez-vous continuer ?
                </div>
            </Modal>
        </>
    );
};

export default ShipmentTrack;
